"use client";

import { useEffect, useMemo, useRef } from "react";
import { useStore } from "react-redux";
import { RelayEnvironmentProvider } from "react-relay";

import { authLogout, checkAuth } from "@skydio/api_util/src/backends/cloud_api/auth/asyncThunks";

import makeEnvironment, { makeSubscriptionClient } from "relay/jwtEnvironment";
import { relayContext } from "relay/relayContext";
import { isAuthenticated } from "state/auth";

import type { ReactNode } from "react";
import type { SubscriptionClient } from "subscriptions-transport-ws";
import type { IEnvironment } from "relay-runtime";
import type { AppStore } from "state/store";

export default function RelayProvider({ children }: { children: ReactNode }) {
  const store: AppStore = useStore();
  const environmentRef = useRef<IEnvironment>(null);
  const subscriptionClientRef = useRef<SubscriptionClient>(null);
  const cleanupRef = useRef<() => void>(null);

  if (!subscriptionClientRef.current) {
    const { client, cleanup } = makeSubscriptionClient(() => {
      if (isAuthenticated(store.getState())) {
        store.dispatch(checkAuth());
      }
    });
    subscriptionClientRef.current = client;
    cleanupRef.current = cleanup;
  }

  if (!environmentRef.current) {
    environmentRef.current = makeEnvironment({
      subscriptionClient: subscriptionClientRef.current,
      onJwtExpired: () => {
        // When expired, explicitly invoke the redux logout logic; we want to go back to the
        // login page.
        store.dispatch(authLogout());
      },
    });
  }

  const contextValue = useMemo(
    () => ({
      subscriptionClient: subscriptionClientRef.current!,
    }),
    []
  );

  // Cleanup subscription client on unmount
  useEffect(() => {
    return () => {
      // Call the cleanup function
      cleanupRef.current?.();
      // Close the subscription client
      subscriptionClientRef.current?.close();
    };
  }, []);

  return (
    <relayContext.Provider value={contextValue}>
      <RelayEnvironmentProvider environment={environmentRef.current}>
        {children}
      </RelayEnvironmentProvider>
    </relayContext.Provider>
  );
}
